import { useAuth0 } from "@auth0/auth0-react";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { getAuth0Config } from "config/api";
import { ToastContext } from "context/ToastContext";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ReactNode, useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useSearchParams } from "react-router-dom";
import {
  usePreloginMutation,
  usePreloginVerifyMutation,
} from "store/queries/auth";
import styled from "styled-components";

import { AuthShell } from "../../components/AuthShell";
import {
  AccessDisabledErrorConst,
  GenericLoginErrorConst,
  InvalidCodeErrorConst,
  TooManyAttemptsForCodeErrorConst,
  TooManyRequestsForCodeErrorConst,
} from "./mfaValidationConstants";

const mfaCookieName = "mfa-last-send-attempts";

// PrimeFlex has no brand-colour utility, and inline styles outrank the theme.
const LINK_STYLE = { color: "var(--brand-purple-700)" };

// Lucide "key-round". PrimeIcons carries no stroked key, and the SPA has no icon
// library, so the one the design calls for is inlined.
const keyRoundIcon = (
  <svg
    aria-hidden="true"
    fill="none"
    height="24"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
    <circle cx="16.5" cy="7.5" fill="currentColor" r=".5" />
  </svg>
);
dayjs.extend(utc);

// Centred, with the digits given room to breathe — a transposed one is easy to
// spot, and the field reads as a code entry rather than a text box.
const CodeInputStyled = styled(InputText)`
  && {
    width: 100%;

    padding: 0.875rem 1rem;

    border: 1px solid var(--brand-gray-200);
    border-radius: 10px;
    background-color: var(--brand-gray-50);

    font-size: 1.125rem;
    font-weight: var(--font-weight-semibold);
    letter-spacing: 0.3em;
    text-align: center;
  }

  /* The field's type scale is for the six digits, not for the prompt. */
  &&::placeholder {
    font-size: 0.9375rem;
    font-weight: 400;
    letter-spacing: normal;
  }

  /* The fill above outranks the theme's invalid state, so it is restated here.
     Same red as .p-inputtext.p-invalid.p-component. */
  &&.p-invalid {
    border-color: #e24c4c;
  }
`;

// Keeps the address out of a shoulder-surfer's reach while still telling the
// right person which inbox to open. Fixed-length mask, so it leaks no length.
const maskEmail = (email: string): string => {
  const [local, domain] = email.split("@");

  if (!domain || !local) {
    return "";
  }

  return `${local.slice(0, 2)}\u2022\u2022\u2022\u2022@${domain}`;
};

export function ValidateMFA(): JSX.Element {
  const { logout } = useAuth0();
  const [code, setCode] = useState<string>("");
  const { current: toastElement } = useContext(ToastContext);
  const [codeError, setCodeError] = useState<string>("");
  const [mfaChannel, setMfaChannel] = useState<"email" | "sms">("email");
  const [mfaEmail, setMfaEmail] = useState<string>("");
  const [mfaPhoneLast4, setMfaPhoneLast4] = useState<string>("");

  const [searchParams] = useSearchParams();

  const state = searchParams.get("state");
  const token = searchParams.get("session_token") || "";

  const [preLogin, { error: preLoginError }] = usePreloginMutation();
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [
    preLoginVerify,
    { error: preLoginVerifyError, isLoading: preLoginVerifyLoading },
  ] = usePreloginVerifyMutation();

  const [cookies, setCookie] = useCookies([mfaCookieName]);
  const sendCode = async (autoCodeSend: boolean) => {
    if (autoCodeSend) {
      // don't auto code send if the code has been sent already in last 5 minutes
      // this is to avoid situations where the user is just refereshing or navigating back and forth
      const lastSendTimestamp = cookies[mfaCookieName];
      if (
        lastSendTimestamp &&
        dayjs.utc(lastSendTimestamp).add(3, "minute").isAfter(dayjs.utc())
      ) {
        return;
      }
    }
    const response = await preLogin({ token }).unwrap();
    setMfaChannel(response.channel || "email");
    setMfaEmail(response.email || "");
    setMfaPhoneLast4(response.phoneLast4 || "");
    setResendLoading(false);
    if (autoCodeSend) {
      setCookie(mfaCookieName, `${dayjs.utc().toISOString()}`);
    }
  };

  const verifyCode = async () => {
    try {
      await preLoginVerify({ token, code }).unwrap();
      window.location.assign(`${getAuth0Config().url}/continue?state=${state}`);
    } catch (e) {
      console.error(e);
    }
  };

  const resendCode = async () => {
    setResendLoading(true);
    await sendCode(false);
  };

  const reSignIn = async () => {
    logout();
  };

  useEffect(() => {
    async function sendCodeToEmail() {
      await sendCode(true);
    }

    sendCodeToEmail();
  }, [state]);

  useEffect(() => {
    if (
      preLoginVerifyError &&
      (preLoginVerifyError as FetchBaseQueryError)?.status === 400
    ) {
      setCodeError(InvalidCodeErrorConst.message);
      return;
    }
    if (
      preLoginVerifyError &&
      (preLoginVerifyError as FetchBaseQueryError)?.status === 429
    ) {
      setCodeError(TooManyAttemptsForCodeErrorConst.message);
      return;
    }

    setCodeError("");
    if (
      preLoginError &&
      (preLoginError as FetchBaseQueryError)?.status === 403
    ) {
      toastElement?.show({
        severity: "error",
        summary: AccessDisabledErrorConst.title,
        sticky: true,
        detail: AccessDisabledErrorConst.message,
      });
      return;
    }
    if (
      preLoginError &&
      (preLoginError as FetchBaseQueryError)?.status === 429
    ) {
      toastElement?.show({
        severity: "error",
        summary: TooManyRequestsForCodeErrorConst.title,
        sticky: true,
        detail: TooManyRequestsForCodeErrorConst.message,
      });
      return;
    }
    //
    if (
      (preLoginError &&
        (preLoginError as FetchBaseQueryError)?.status === 401) ||
      (preLoginVerifyError &&
        (preLoginVerifyError as FetchBaseQueryError)?.status === 401)
    ) {
      // auth0 code has expired because the user has been sitting on the page for too long
      reSignIn();
    }

    if (preLoginVerifyError || preLoginError) {
      toastElement?.show({
        severity: "error",
        summary: GenericLoginErrorConst.title,
        sticky: true,
        detail: GenericLoginErrorConst.message,
      });
    }
  }, [preLoginError, preLoginVerifyError]);

  // The code goes to the phone on the sms channel, so the address on file is the
  // wrong thing to name there. Both branches fall back to the generic wording when
  // the send was skipped (a recent code is still valid) and there is nothing to show.
  const maskedEmail = mfaChannel === "email" ? maskEmail(mfaEmail) : "";
  let destination: ReactNode = "the email address on file";
  if (mfaChannel === "sms") {
    destination = mfaPhoneLast4 ? (
      <>
        your phone number ending{" "}
        <strong style={{ color: "var(--brand-black)" }}>
          {"\u2022\u2022"}
          {mfaPhoneLast4}
        </strong>
      </>
    ) : (
      "the phone number on file"
    );
  } else if (maskedEmail) {
    destination = (
      <strong style={{ color: "var(--brand-black)" }}>{maskedEmail}</strong>
    );
  }

  return (
    <AuthShell
      brand={{
        eyebrow: "Protected access",
        headline: "One more step keeps patient data safer.",
        text: "Two-factor authentication helps ensure that only authorized staff can access your revenue workspace.",
      }}
      eyebrow="Two-factor authentication"
      icon={keyRoundIcon}
      subtitle={<>Enter the 6-digit code we sent to {destination}.</>}
      title="Enter your security code"
    >
      <div>
        <label
          className="block mb-2 text-sm font-semibold"
          htmlFor="code"
          style={{ color: "var(--brand-black)" }}
        >
          Security code
        </label>
        <CodeInputStyled
          autoComplete="one-time-code"
          className={codeError !== "" ? "p-invalid" : ""}
          id="code"
          inputMode="numeric"
          maxLength={6}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Enter 6-digit code"
          required
        />
        {codeError !== "" && (
          <small className="p-error block mt-2">{codeError}</small>
        )}
      </div>

      <Button
        className="w-full justify-content-center py-3 mt-3 p-button-rounded-md"
        icon="pi pi-arrow-right"
        iconPos="right"
        label="Verify and continue"
        loading={preLoginVerifyLoading}
        onClick={verifyCode}
        type="button"
      />

      <div
        className="mt-4 p-3 border-1 border-round-xl text-sm line-height-3"
        style={{
          borderColor: "var(--brand-gray-200)",
          backgroundColor: "var(--brand-gray-50)",
          color: "var(--brand-gray-700)",
        }}
      >
        This code is for you only — never share it with anyone. Need help?
        Contact your hospital administrator.
      </div>

      <div className="flex align-items-center justify-content-between mt-4">
        <Button
          className="p-button-link p-0 text-sm"
          icon="pi pi-arrow-left"
          label="Back to sign in"
          onClick={reSignIn}
          style={LINK_STYLE}
          type="button"
        />
        <Button
          className="p-button-link p-0 text-sm"
          label="Resend Code"
          loading={resendLoading}
          onClick={resendCode}
          style={LINK_STYLE}
          type="button"
        />
      </div>
    </AuthShell>
  );
}
